import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Eye, PlugZap, RefreshCw } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { Chip, PageHeader, ReasonsList, RestrictedState, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ACTIVITY_LABELS, RUN6_STATUS, SIG6_SEV, SIG6_STATUS, fmtDateTime } from "../lib/labels";

const VIEWS = [["executive", "Executive"], ["legal", "Legal"], ["projects", "Projects"], ["finance", "Finance"], ["corporate_operations", "Corporate Operations"], ["my_dashboard", "Mi cuadro"]];
const MODULE_ROUTE = { documentos: "/documentos/corp", correo: "/correo", polizas: "/polizas", vacaciones: "/vacaciones", automatizaciones: "/control-corporativo" };

export default function ControlCorp() {
  const { user } = useAuth();
  const { map, users } = useUsers();
  const qc = useQueryClient();
  const [view, setView] = useState("executive");
  const [dlg, setDlg] = useState(null);
  const [f, setF] = useState({});
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const refresh = () => qc.invalidateQueries();
  const { data: d, error: cuadroErr } = useQuery({ queryKey: ["corp", view], queryFn: async () => (await api.get("/corporativo/cuadro", { params: { view } })).data, refetchInterval: 60000 });
  const { data: runs } = useQuery({ queryKey: ["corp-runs"], queryFn: async () => (await api.get("/corporativo/automatizaciones")).data });

  const mut = useMutation({
    mutationFn: async ({ path, body }) => (await api.post(path, body || {})).data,
    onSuccess: () => { toast.success("Operación aplicada"); setDlg(null); setF({}); setErr(null); refresh(); },
    onError: (e) => { setErr(e); toast.error(apiError(e) || "Error"); },
  });
  const open = (x) => { setDlg(x); setF({}); setErr(null); };
  const act = (sid, action, extra = {}) => mut.mutate({ path: `/corporativo/senales/${sid}/accion`, body: { action, ...extra } });

  if (user?.is_contractor || cuadroErr?.response?.status === 403) {
    return (
      <div className="space-y-5" data-testid="control-corp-page">
        <PageHeader title="Control corporativo" subtitle="" testid="corp-title" />
        <RestrictedState module="el cuadro corporativo" detail={cuadroErr?.response?.data?.detail} testid="corp-restricted" />
      </div>
    );
  }

  const sigCard = (s) => (
    <div key={s.id} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`sig-${s.id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`font-display text-lg font-bold ${s.priority_score_effective >= 60 ? "text-red-700" : s.priority_score_effective >= 35 ? "text-amber-700" : "text-slate-700"}`} data-testid={`sig-score-${s.id}`}>{s.priority_score_effective}</span>
        <Chip map={SIG6_SEV} value={s.severity} />
        <Chip map={SIG6_STATUS} value={s.status} testid={`sig-status-${s.id}`} />
        <ToneChip label={s.source_module} tone="info" />
        {s.override_active && <ToneChip label="prioridad ajustada (override)" tone="warning" />}
        {s.blocked_process && <ToneChip label={`bloquea: ${s.blocked_process}`} tone="danger" />}
      </div>
      <p className="mt-1 text-sm font-medium text-slate-800">{s.title}</p>
      <p className="text-xs text-slate-500">{s.summary}</p>
      <p className="mt-0.5 text-xs text-slate-400">
        responsable {userName(map, s.owner_id) || "sin asignar"}
        {s.due_date ? ` · fecha ${String(s.due_date).slice(0, 10)}` : ""} · acción recomendada: {s.recommended_action || "—"}
      </p>
      <button className="mt-1 text-xs text-blue-700 hover:underline" data-testid={`sig-factors-${s.id}`} onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
        {expanded === s.id ? "Ocultar factores de prioridad" : "¿Por qué esta prioridad?"}
      </button>
      {expanded === s.id && (
        <ul className="mt-1 space-y-0.5 rounded border border-slate-100 bg-slate-50 p-2" data-testid={`sig-factors-list-${s.id}`}>
          {(s.priority_factors || []).map((fa, i) => <li key={i} className="text-xs text-slate-600">+{fa.weight} · {fa.factor}{fa.note ? ` — ${fa.note}` : ""}</li>)}
          {s.override && <li className="text-xs text-amber-700">Override: {s.override.previous} → {s.override.new_score} por {userName(map, s.override.by)}: {s.override.motivo} (caduca {String(s.override.expires_at).slice(0, 10)})</li>}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap gap-1">
        {s.action_route && <Link to={s.action_route}><Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid={`sig-open-${s.id}`}><ArrowRight className="mr-1 h-3 w-3" /> Abrir origen</Button></Link>}
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`sig-ack-${s.id}`} onClick={() => act(s.id, "reconocer")}>Reconocer</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`sig-assign-${s.id}`} onClick={() => open({ kind: "asignar", sig: s })}>Asignar</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`sig-task-${s.id}`} onClick={() => act(s.id, "crear_tarea")}>Crear tarea</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`sig-postpone-${s.id}`} onClick={() => open({ kind: "posponer", sig: s })}>Posponer</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" data-testid={`sig-resolve-${s.id}`} onClick={() => act(s.id, "resolver")}>Resolver</Button>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-slate-500" data-testid={`sig-fp-${s.id}`} onClick={() => open({ kind: "fp", sig: s })}>Falso positivo</Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="control-corp-page">
      <PageHeader title="Control corporativo"
        subtitle="Agregador transversal: cada condición vive en su módulo y solo se resuelve en origen. Prioridad determinista con factores visibles (configuración demo)."
        testid="corp-title"
        actions={<>
          <Button variant="outline" size="sm" onClick={() => mut.mutate({ path: "/corporativo/visto" })} data-testid="corp-visto"><Eye className="mr-1 h-4 w-4" /> Visto hasta aquí</Button>
          <Button variant="outline" size="sm" onClick={() => mut.mutate({ path: "/corporativo/senales/refresh" })} data-testid="corp-refresh"><RefreshCw className="mr-1 h-4 w-4" /> Actualizar</Button>
          <Link to="/resumen/corporate/corporativo"><Button variant="outline" size="sm" data-testid="corp-brief-btn">Executive brief</Button></Link>
          <Button size="sm" onClick={() => open({ kind: "run" })} data-testid="corp-run-btn"><PlugZap className="mr-1 h-4 w-4" /> Ejecutar automatización</Button>
        </>} />

      <div className="flex flex-wrap gap-1" data-testid="corp-views">
        {VIEWS.map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${view === k ? "bg-blue-700 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
        ))}
        {d && <span className="ml-auto text-xs text-slate-400" data-testid="corp-freshness">Datos: {d.freshness.status} · actualizado {fmtDateTime(d.freshness.updated_at)}</span>}
      </div>

      {d && (
        <>
          <section data-testid="corp-atencion">
            <h2 className="ts-section-title mb-2">1 · Requiere atención ({d.requiere_atencion.length})</h2>
            <div className="space-y-2">{d.requiere_atencion.map(sigCard)}
              {!d.requiere_atencion.length && <p className="text-sm text-slate-500">Nada requiere atención en esta vista.</p>}
            </div>
          </section>

          <section data-testid="corp-bloqueados">
            <h2 className="ts-section-title mb-2">2 · Procesos bloqueados ({d.procesos_bloqueados.length})</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {d.procesos_bloqueados.map((s) => (
                <div key={s.id} className="rounded-md border border-red-200 bg-red-50 p-3" data-testid={`blocked-${s.id}`}>
                  <p className="text-sm font-semibold text-red-800">{s.blocked_process}</p>
                  <p className="text-xs text-red-700">Motivo: {s.summary}</p>
                  <p className="text-xs text-slate-500">Responsable: {userName(map, s.owner_id)} · {s.recommended_action}</p>
                  {s.action_route && <Link to={s.action_route} className="text-xs text-blue-700 hover:underline" data-testid={`blocked-open-${s.id}`}>Abrir para resolver →</Link>}
                </div>
              ))}
              {!d.procesos_bloqueados.length && <p className="text-sm text-slate-500">Sin procesos bloqueados.</p>}
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-2">
            <section data-testid="corp-mi-trabajo">
              <h2 className="ts-section-title mb-2">3 · Mi trabajo</h2>
              <div className="grid grid-cols-3 gap-2">
                {[["Tareas abiertas", d.mi_trabajo.tareas_abiertas, "/mi-trabajo"], ["Vencidas", d.mi_trabajo.tareas_vencidas, "/mi-trabajo?vista=vencido"], ["En revisión", d.mi_trabajo.esperando_revision, "/mi-trabajo?vista=revision"]].map(([l, v, to]) => (
                  <Link key={l} to={to} className="ts-card ts-card-hover p-3" data-testid={`corp-mt-${l}`}><p className="ts-caption">{l}</p><p className="font-display text-2xl font-bold">{v}</p></Link>
                ))}
              </div>
              <Link to="/aprobaciones?vista=mias" className="ts-card ts-card-hover mt-2 block p-3" data-testid="corp-approvals">
                <p className="ts-caption">4 · Aprobaciones y firmas que esperan mi decisión</p>
                <p className="font-display text-2xl font-bold">{d.aprobaciones_pendientes}</p>
              </Link>
            </section>

            <section data-testid="corp-modulos">
              <h2 className="ts-section-title mb-2">5–8 · Documentación, comunicaciones, pólizas, personas</h2>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                {Object.entries(d.conteos).map(([k, v]) => (
                  <Link key={k} to={MODULE_ROUTE[k] || "/control-corporativo"} className="ts-card ts-card-hover p-3" data-testid={`corp-count-${k}`}>
                    <p className="ts-caption">{k.replaceAll("_", " ")}</p>
                    <p className={`font-display text-2xl font-bold ${v ? "text-slate-900" : "text-slate-400"}`}>{v}</p>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <section data-testid="corp-cambios">
              <h2 className="ts-section-title mb-2">10 · Qué ha cambiado desde tu último acceso</h2>
              <div className="ts-card max-h-64 space-y-1.5 overflow-y-auto p-3">
                {d.que_ha_cambiado.map((c) => (
                  <p key={c.id} className="text-xs" data-testid={`change-${c.id}`}>
                    <span className="font-medium text-slate-700">{ACTIVITY_LABELS[c.action] || c.action}</span>
                    <span className="text-slate-500"> — {c.detail}</span>
                    <span className="text-slate-400"> · {fmtDateTime(c.timestamp)}</span>
                  </p>
                ))}
                {!d.que_ha_cambiado.length && <p className="text-xs text-slate-400">Sin cambios desde tu último acceso.</p>}
              </div>
            </section>

            <section data-testid="corp-integraciones">
              <h2 className="ts-section-title mb-2">11 · Salud de integraciones (mocks)</h2>
              <div className="ts-card space-y-1.5 p-3">
                {d.integraciones.map((i) => (
                  <div key={i.name} className="flex items-center gap-2 text-xs" data-testid={`int-${i.name}`}>
                    <span className={`h-2 w-2 rounded-full ${i.status === "healthy" || i.status === "conectado" ? "bg-emerald-500" : i.status === "degradado" ? "bg-amber-500" : "bg-red-500"}`} />
                    <span className="font-medium">{i.name}</span>
                    <span className="text-slate-400">{i.provider} · {i.status}</span>
                    {(i.errors || []).map((e, j) => <span key={j} className="text-red-600">{e.message}</span>)}
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section data-testid="corp-automatizaciones">
            <h2 className="ts-section-title mb-2">12 · Automatizaciones</h2>
            <div className="space-y-1.5">
              {(runs || []).map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-2" data-testid={`run-${r.id}`}>
                  <Chip map={RUN6_STATUS} value={r.status} testid={`run-status-${r.id}`} />
                  <span className="text-xs font-medium">{r.trigger}</span>
                  <span className="text-xs text-slate-400">{r.source_id} · {fmtDateTime(r.started_at)} · completadas: {(r.actions_completed || []).length}</span>
                  {["failed", "dead_letter"].includes(r.status) && (
                    <>
                      <span className="text-xs text-red-600">Falló en «{r.failed_step}». Qué falta: reintentar la parte fallida (las acciones completadas no se repiten).</span>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid={`run-retry-${r.id}`} onClick={() => mut.mutate({ path: `/corporativo/automatizaciones/${r.id}/reintentar` })}><RefreshCw className="mr-1 h-3 w-3" /> Reintentar</Button>
                    </>
                  )}
                </div>
              ))}
              {runs && !runs.length && <p className="text-sm text-slate-500">Sin ejecuciones registradas.</p>}
            </div>
          </section>
        </>
      )}

      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent data-testid="corp-dialog">
          <DialogHeader><DialogTitle>
            {dlg?.kind === "asignar" && `Asignar — ${dlg.sig.title}`}
            {dlg?.kind === "posponer" && `Posponer — ${dlg.sig.title}`}
            {dlg?.kind === "fp" && `Descartar como falso positivo`}
            {dlg?.kind === "run" && "Ejecutar automatización (demo)"}
          </DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              {dlg.kind === "asignar" && (
                <div><Label>Responsable *</Label>
                  <Select value={f.owner_id || ""} onValueChange={(v) => setF({ ...f, owner_id: v })}>
                    <SelectTrigger data-testid="f-owner"><SelectValue placeholder="Responsable" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dlg.kind === "posponer" && (
                <>
                  {(dlg.sig.severity === "critica" || dlg.sig.blocked_process) && <p className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">Señal crítica o bloqueante: posponer requiere autorización expresa.</p>}
                  <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>
                  <div><Label>Reaparece el *</Label><Input type="date" value={f.reappear_at || ""} onChange={(e) => setF({ ...f, reappear_at: e.target.value })} data-testid="f-reappear" /></div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.override_autorizado} onChange={(e) => setF({ ...f, override_autorizado: e.target.checked })} data-testid="f-override" /> Autorización expresa para posponer elemento crítico</label>
                </>
              )}
              {dlg.kind === "fp" && <div><Label>Motivo *</Label><Input value={f.motivo || ""} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="f-motivo" /></div>}
              {dlg.kind === "run" && (
                <>
                  <div><Label>Trigger *</Label>
                    <Select value={f.trigger || ""} onValueChange={(v) => setF({ ...f, trigger: v })}>
                      <SelectTrigger data-testid="f-trigger"><SelectValue placeholder="Trigger" /></SelectTrigger>
                      <SelectContent>{["policy_expiry_approaching", "email_response_overdue", "repository_sync_error", "document_review_due", "vacation_approved"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Source ID</Label><Input value={f.source_id || ""} onChange={(e) => setF({ ...f, source_id: e.target.value })} data-testid="f-source" placeholder="barrido / repo_erp / threads" /></div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.fail_demo} onChange={(e) => setF({ ...f, fail_demo: e.target.checked })} data-testid="f-fail" /> Simular fallo (genera dead letter recuperable)</label>
                </>
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="corp-error">{apiError(err)}</p>)}
              <Button data-testid="corp-dialog-submit" disabled={mut.isPending}
                onClick={() => {
                  if (dlg.kind === "asignar") act(dlg.sig.id, "asignar", f);
                  else if (dlg.kind === "posponer") act(dlg.sig.id, "posponer", f);
                  else if (dlg.kind === "fp") act(dlg.sig.id, "falso_positivo", f);
                  else if (dlg.kind === "run") mut.mutate({ path: "/corporativo/automatizaciones/ejecutar", body: f });
                }}><Check className="mr-1 h-4 w-4" /> Confirmar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
